import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type AdminRole = 'super_admin' | 'admin' | 'site_admin' | null;

interface AdminAuthState {
  isLoading: boolean;
  user: any;
  profile: any;
  role: AdminRole;
  companyId: string | null;
  assignedSiteIds: string[];
}

interface SiteAssignment {
  site_id: string;
}

export const useAdminAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, setState] = useState<AdminAuthState>({
    isLoading: true,
    user: null,
    profile: null,
    role: null,
    companyId: null,
    assignedSiteIds: [],
  });

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }

      const userId = session.user.id;

      // Check user roles
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (!roles || roles.length === 0) {
        toast({
          title: "Access Denied",
          description: "You don't have admin privileges.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Determine primary role (priority: super_admin > admin > site_admin)
      const roleList = roles.map(r => r.role);
      let primaryRole: AdminRole = null;
      
      if (roleList.includes('super_admin')) {
        primaryRole = 'super_admin';
      } else if (roleList.includes('admin')) {
        primaryRole = 'admin';
      } else if (roleList.includes('site_admin')) {
        primaryRole = 'site_admin';
      }

      if (!primaryRole) {
        toast({
          title: "Access Denied",
          description: "You don't have admin privileges.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Load profile
      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('*, company_locations(location_name)')
        .eq('user_id', userId)
        .maybeSingle();

      const companyId = profile?.company_id || null;

      // If site_admin, get assigned sites
      let assignedSiteIds: string[] = [];
      if (primaryRole === 'site_admin') {
        const { data: siteAssignments } = await supabase
          .from('site_admin_assignments')
          .select('site_id')
          .eq('user_id', userId);

        if (siteAssignments) {
          assignedSiteIds = (siteAssignments as SiteAssignment[]).map(s => s.site_id);
        }

        if (assignedSiteIds.length === 0) {
          toast({
            title: "No Sites Assigned",
            description: "You are not assigned to any sites.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }
      }

      setState({
        isLoading: false,
        user: session.user,
        profile,
        role: primaryRole,
        companyId,
        assignedSiteIds,
      });

    } catch (error) {
      console.error('Admin auth error:', error);
      toast({
        title: "Authentication Error",
        description: "Please try logging in again.",
        variant: "destructive",
      });
      navigate('/admin/login');
    }
  };

  const canAccessSite = (siteId: string): boolean => {
    if (state.role === 'super_admin') return true;
    if (state.role === 'admin') {
      // Company admin can access all sites in their company
      // This would require additional check against company_locations
      return true;
    }
    if (state.role === 'site_admin') {
      return state.assignedSiteIds.includes(siteId);
    }
    return false;
  };

  const canManageUsers = (siteId?: string): boolean => {
    if (state.role === 'super_admin') return true;
    if (state.role === 'admin') return true;
    if (state.role === 'site_admin' && siteId) {
      return state.assignedSiteIds.includes(siteId);
    }
    return false;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  return {
    ...state,
    canAccessSite,
    canManageUsers,
    logout,
    refresh: checkAdminAuth,
  };
};